/**
 * Lightbox xem ảnh BĐS — gallery, zoom, pan và điều hướng bằng phím/chuột.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatText, TEXT } from '../../shared/languages';

const MIN_SCALE = 1;
const MAX_SCALE = 3;
const SCALE_STEP = 0.25;

export const ImageLightbox = ({
  images,
  initialIndex = 0,
  alt = '',
  className = '',
  onClose,
}) => {
  const t = TEXT.property.detail;
  const common = TEXT.common;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef(null);
  const scaleRef = useRef(scale);

  scaleRef.current = scale;

  const imageList = images.length ? images : [];
  const hasMultipleImages = imageList.length > 1;
  const currentImage = imageList[currentIndex];
  const canPan = scale > MIN_SCALE;

  const goToPrevious = useCallback(() => {
    setCurrentIndex((index) => (index === 0 ? imageList.length - 1 : index - 1));
  }, [imageList.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((index) => (index === imageList.length - 1 ? 0 : index + 1));
  }, [imageList.length]);

  const zoomIn = useCallback(() => {
    setScale((currentScale) => Math.min(currentScale + SCALE_STEP, MAX_SCALE));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((currentScale) => Math.max(currentScale - SCALE_STEP, MIN_SCALE));
  }, []);

  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [currentIndex]);

  useEffect(() => {
    if (scale <= MIN_SCALE) {
      setOffset({ x: 0, y: 0 });
      dragStateRef.current = null;
      setIsDragging(false);
    }
  }, [scale]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }

      if (event.key === 'ArrowLeft' && hasMultipleImages) {
        goToPrevious();
      }

      if (event.key === 'ArrowRight' && hasMultipleImages) {
        goToNext();
      }

      if (event.key === '+' || event.key === '=') {
        zoomIn();
      }

      if (event.key === '-') {
        zoomOut();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [goToNext, goToPrevious, hasMultipleImages, onClose, zoomIn, zoomOut]);

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const handlePointerMove = (event) => {
      if (!dragStateRef.current || scaleRef.current <= MIN_SCALE) {
        return;
      }

      const deltaX = event.clientX - dragStateRef.current.startX;
      const deltaY = event.clientY - dragStateRef.current.startY;

      setOffset({
        x: dragStateRef.current.originX + deltaX,
        y: dragStateRef.current.originY + deltaY,
      });
    };

    const handlePointerUp = () => {
      dragStateRef.current = null;
      setIsDragging(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging]);

  const handlePointerDown = (event) => {
    if (scaleRef.current <= MIN_SCALE || event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };
    setIsDragging(true);
  };

  if (!currentImage) {
    return null;
  }

  const lightboxClassName = ['image-lightbox', className].filter(Boolean).join(' ');

  return (
    <div className={lightboxClassName} role="dialog" aria-modal="true" aria-label={t.galleryMainAlt}>
      <div className="image-lightbox-toolbar">
        <span className="image-lightbox-counter">
          {formatText(t.lightboxCounter, {
            current: currentIndex + 1,
            total: imageList.length,
          })}
        </span>
        <div className="image-lightbox-controls">
          <button className="image-lightbox-button" type="button" onClick={zoomOut} aria-label={t.lightboxZoomOut}>
            −
          </button>
          <span className="image-lightbox-scale">{Math.round(scale * 100)}%</span>
          <button className="image-lightbox-button" type="button" onClick={zoomIn} aria-label={t.lightboxZoomIn}>
            +
          </button>
          <button className="image-lightbox-button" type="button" onClick={onClose} aria-label={common.close}>
            ×
          </button>
        </div>
      </div>

      <div className="image-lightbox-stage" onClick={onClose}>
        {hasMultipleImages && (
          <button
            className="image-lightbox-nav image-lightbox-nav--prev"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              goToPrevious();
            }}
            aria-label={t.lightboxPrev}
          >
            ‹
          </button>
        )}

        <div
          className={`image-lightbox-frame ${canPan ? 'is-pannable' : ''} ${isDragging ? 'is-dragging' : ''}`}
          onClick={(event) => event.stopPropagation()}
          onPointerDown={canPan ? handlePointerDown : undefined}
          onWheel={(event) => {
            event.preventDefault();

            if (event.deltaY < 0) {
              zoomIn();
            } else {
              zoomOut();
            }
          }}
        >
          <img
            src={currentImage}
            alt={alt}
            className="image-lightbox-image"
            style={{
              transform: canPan
                ? `translate(${offset.x}px, ${offset.y}px) scale(${scale})`
                : `scale(${scale})`,
            }}
            draggable={false}
          />
        </div>

        {hasMultipleImages && (
          <button
            className="image-lightbox-nav image-lightbox-nav--next"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              goToNext();
            }}
            aria-label={t.lightboxNext}
          >
            ›
          </button>
        )}
      </div>
    </div>
  );
};
